import React, { useState, useEffect } from 'react';
import { Search, Filter, LayoutGrid, Plus, PenSquare, Trash2, Check, X, Calendar, ShieldCheck, List } from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import ThemeSelect from '../components/ThemeSelect';
import { productsApi } from '../services/api';
import Pagination from '../components/Pagination';



const ProductCategory = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [searchTerm, setSearchTerm] = useState('');
  /* State for managing Modal mode (Add/Edit) & Data */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null); // If null, it's ADD mode
  const [modalInputName, setModalInputName] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingCategoryName, setDeletingCategoryName] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    status: 'All Status',
    date: '',
    categoryName: 'All Categories'
  });
  const [tempFilters, setTempFilters] = useState(activeFilters);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toggleStatus = async (id) => {
    try {
      await productsApi.toggleProductCategoryStatus(id);
      setCategories(categories.map(cat =>
        (cat.id === id || cat._id === id) ? { ...cat, status: !cat.status, isActive: !cat.isActive } : cat
      ));
      message.success('Status updated');
    } catch (err) {
      console.error('Error toggling status:', err);
      message.error('Failed to update status');
    }
  };
  useEffect(() => {
    fetchCategories();
  }, [searchTerm]);

  // Sync tempFilters when modal opens
  useEffect(() => {
    if (isFilterModalOpen) {
      setTempFilters(activeFilters);
    }
  }, [isFilterModalOpen, activeFilters]);

  // Filter logic
  const filteredCategories = categories.filter(cat => {
    const matchesSearch =
      cat.id.toString().includes(searchTerm) ||
      cat.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      activeFilters.status === 'All Status' ||
      (activeFilters.status === 'Active' && cat.status) ||
      (activeFilters.status === 'Inactive' && !cat.status);

    const matchesDate = !activeFilters.date ||
      (cat.date && cat.date.includes(activeFilters.date.split('-').reverse().join('/')));

    const matchesCategory = activeFilters.categoryName === 'All Categories' ||
      cat.name === activeFilters.categoryName;

    return matchesSearch && matchesStatus && matchesDate && matchesCategory;
  });



  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await productsApi.getAllProductCategories(searchTerm);
      const data = Array.isArray(response) ? response : (response.data || []);
      const mapped = data.map(item => ({
        id: item._id || item.id,
        name: item.categoryName || item.name || 'N/A',
        createdBy: item.createdBy || 'System',
        date: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB') : (item.date || 'N/A'),
        status: item.isActive ?? item.status ?? true,
        isActive: item.isActive ?? item.status ?? true
      }));
      setCategories(mapped);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };
  /* Handlers for Actions */

  // Open Modal for Create
  const handleOpenAddModal = () => {
    setEditingCategory(null);
    setModalInputName('');
    setIsModalOpen(true);
  };

  // Open Modal for Edit
  const handleOpenEditModal = async (category) => {
    try {
      setLoading(true);
      const id = category.id || category._id;
      const res = await productsApi.getProductCategoryById(id);
      const data = res.data || res;
      setEditingCategory(data);
      setModalInputName(data.categoryName || data.name);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching category details:', err);
      // Fallback to list data if API fails
      setEditingCategory(category);
      setModalInputName(category.name);
      setIsModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Delete Category
  const handleDelete = (category) => {
    // Capture ID and Name robustly from the object
    const id = category.id || category._id;
    const name = category.name || category.categoryName || category.category_name || "Category";

    setCategoryToDelete(id);
    setDeletingCategoryName(name);
    setIsDeleteModalOpen(true);
  };

  // Confirm Delete
  const confirmDelete = async () => {
    if (categoryToDelete) {
      setIsDeleting(true);
      try {
        await productsApi.deleteProductCategory(categoryToDelete);
        setCategories(categories.filter(cat => (cat.id !== categoryToDelete && cat._id !== categoryToDelete)));
        message.success(`${deletingCategoryName} deleted successfully`);

        // Only clear state and close modal on SUCCESS
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
        setDeletingCategoryName('');
      } catch (err) {
        console.error('Error deleting category:', err);
        // Extract the most descriptive error message possible
        const errorDetail = err.response?.data?.message || err.message || "Unknown error";
        message.error(`Failed to delete "${deletingCategoryName}": ${errorDetail}`);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Submit Form (Add or Edit)
  const handleSubmit = async () => {
    if (!modalInputName.trim()) return;

    try {
      if (editingCategory) {
        const id = editingCategory.id || editingCategory._id;
        await productsApi.updateProductCategory(id, { categoryName: modalInputName });
        message.success('Category updated successfully');
      } else {
        await productsApi.createProductCategory({ categoryName: modalInputName });
        message.success('Category created successfully');
      }
      fetchCategories();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving category:', err);
      message.error('Failed to save category');
    }
  };

  return (
    <div className={`p-6 desktop:p-8 min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Product category</h1>

        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* Search Bar */}
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by category ID"
          />

          {/* Filter Button */}
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className={`p-2 desktop:p-3 border rounded-full transition-colors ${activeFilters.status !== 'All Status' || activeFilters.date || activeFilters.categoryName !== 'All Categories' ? 'bg-teal-50 border-teal-200' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <Filter className={`w-5 h-5 desktop:w-6 desktop:h-6 ${activeFilters.status !== 'All Status' || activeFilters.date || activeFilters.categoryName !== 'All Categories' ? 'text-teal-600' : 'text-gray-500'}`} />
          </button>

          {/* Add Button */}
          <button
            onClick={handleOpenAddModal}
            className="p-2 desktop:p-3 text-white rounded-full transition-colors relative group"
            style={{ backgroundColor: currentTheme.color }}
          >
            <LayoutGrid className="w-5 h-5 desktop:w-6 desktop:h-6" />
            <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5">
              <Plus className="w-2 h-2 desktop:w-3 desktop:h-3" strokeWidth={4} style={{ color: currentTheme.color }} />
            </div>
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className={`border rounded-xl overflow-hidden transition-all ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-left border-collapse">
            <thead>
              <tr className="text-white text-xs desktop:text-base uppercase font-medium" style={{ backgroundColor: currentTheme.color }}>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">Category ID</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">Category Name</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">Created By</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">Created Date</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide text-center">Status</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide text-center">Action</th>
              </tr>
            </thead>
            <tbody className={`${isDark ? 'divide-y divide-white/5' : 'bg-white'}`}>
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">
                    No categories found
                  </td>
                </tr>
              ) : (
                filteredCategories
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((category) => (
                  <tr key={category.id} className={`border-b transition-colors ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{category.id}</td>
                    <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{category.name}</td>
                    <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{category.createdBy}</td>
                    <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{category.date}</td>

                    {/* Status Toggle */}
                    <td className="py-2 px-6 desktop:py-4 desktop:px-8 text-center">
                      <button
                        onClick={() => toggleStatus(category.id)}
                        className={`relative w-11 h-6 desktop:w-14 desktop:h-8 rounded-full transition-all duration-300 ease-in-out focus:outline-none border-2`}
                        style={{ 
                          backgroundColor: category.status ? currentTheme.color : '#ffffff',
                          borderColor: isDark ? '#ffffff' : '#D1D5DB',
                        }}
                      >
                        <span
                          className={`absolute top-[2px] left-[2px] w-4 h-4 desktop:w-6 desktop:h-6 rounded-full shadow-md transition-all duration-300 ease-in-out transform border-2 border-white ${
                            category.status 
                              ? 'translate-x-5 desktop:translate-x-6 bg-white' 
                              : 'translate-x-0 bg-gray-400'
                          }`}
                        />
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="py-2 px-6 desktop:py-4 desktop:px-8 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => handleOpenEditModal(category)}
                          className="p-1 desktop:p-2 transition-colors"
                          style={{ color: currentTheme.color }}
                        >
                          <PenSquare className="w-5 h-5 desktop:w-6 desktop:h-6" />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="p-1 desktop:p-2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-5 h-5 desktop:w-6 desktop:h-6" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination 
          currentPage={currentPage}
          totalItems={filteredCategories.length}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Add/Edit Category Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ animation: 'fadeIn 0.25s ease-out forwards' }}
        >
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          `}</style>
          <div
            className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-xl shadow-lg w-full max-w-lg desktop:max-w-2xl overflow-hidden transform transition-all`}
            style={{ animation: 'scaleIn 0.25s ease-out forwards' }}
          >
            {/* Modal Header */}
            <div className={`${isDark ? 'bg-white/5' : 'bg-[#C8E6C9]'} px-6 py-4 desktop:px-8 desktop:py-6 flex justify-between items-center border-b ${isDark ? 'border-white/10' : 'border-transparent'}`}>
              <h2 className={`text-xl desktop:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                {editingCategory ? 'Edit Product Category' : 'Add New Product Category'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'} focus:outline-none transition-colors`}
              >
                <X className="w-6 h-6 desktop:w-8 desktop:h-8" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-8 desktop:p-10">
              <div className="mb-8">
                <label className={`block text-sm desktop:text-lg font-bold mb-2 ${isDark ? 'text-gray-300' : 'text-[#2E4F4F]'}`}>
                  Product category name
                </label>
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={modalInputName}
                  onChange={(e) => setModalInputName(e.target.value)}
                  className={`w-full px-4 py-3 desktop:px-6 desktop:py-4 border rounded-lg transition-all focus:outline-none focus:ring-2 text-base desktop:text-xl ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white focus:bg-white/10' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                  style={{ '--tw-ring-color': currentTheme.color }}
                  onFocus={(e) => e.target.style.borderColor = currentTheme.color}
                  onBlur={(e) => e.target.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB'}
                />
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end">
                <button
                  className="text-white px-8 py-2.5 desktop:px-10 desktop:py-4 rounded-lg text-lg desktop:text-xl font-medium transition-colors shadow-sm"
                  style={{ backgroundColor: currentTheme.color }}
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          style={{ animation: 'fadeIn 0.25s ease-out forwards' }}
        >
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-md desktop:max-w-lg overflow-hidden transform transition-all"
            style={{ animation: 'scaleIn 0.25s ease-out forwards' }}
          >
            <div className="p-6 desktop:p-8 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 desktop:h-16 desktop:w-16 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 desktop:h-8 desktop:w-8 text-red-600" />
              </div>
              <h3 className="text-xl desktop:text-2xl font-medium text-gray-900 mb-2">Delete "{deletingCategoryName}"?</h3>
              <p className="text-sm desktop:text-lg text-gray-500 mb-6 font-medium">
                Are you sure you want to delete this category? All associated products might be affected.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setCategoryToDelete(null);
                    setDeletingCategoryName('');
                  }}
                  disabled={isDeleting}
                  className="px-6 py-2.5 desktop:px-8 desktop:py-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-base desktop:text-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="px-6 py-2.5 desktop:px-8 desktop:py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm text-base desktop:text-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : 'Delete Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div
            className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-xl shadow-lg w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-scaleIn`}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: currentTheme.color }}>
              <div className="flex items-center gap-2 text-white">
                <Filter className="w-5 h-5" />
                <h2 className="text-xl font-bold">Filter Categories</h2>
              </div>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Category Name Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <List className="w-4 h-4 text-teal-600" />
                  CATEGORY NAME
                </label>
                <ThemeSelect
                  value={tempFilters.categoryName}
                  onChange={(val) => setTempFilters({ ...tempFilters, categoryName: val })}
                  options={['All Categories', ...categories.map(c => c.name)]}
                  className="w-full"
                  icon={LayoutGrid}
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  STATUS
                </label>
                <div className="flex gap-2">
                  {['All Status', 'Active', 'Inactive'].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setTempFilters({ ...tempFilters, status: opt })}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${tempFilters.status === opt
                        ? 'border-teal-600 bg-teal-50 text-teal-700'
                        : isDark 
                          ? 'border-white/10 text-gray-400 bg-white/5 hover:bg-white/10' 
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Calendar className="w-4 h-4 text-teal-600" />
                  CREATED DATE
                </label>
                <input
                  type="date"
                  value={tempFilters.date}
                  onChange={(e) => setTempFilters({ ...tempFilters, date: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${
                    isDark 
                      ? 'bg-white/5 border-white/10 text-white' 
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                  style={{ '--tw-ring-color': currentTheme.color }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t rounded-b-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
              <button
                onClick={() => {
                  setTempFilters({
                    status: 'All Status',
                    date: '',
                    categoryName: 'All Categories'
                  });
                }}
                className={`px-5 py-2.5 text-sm font-bold rounded-xl transition-all active:scale-95 border ${
                  isDark 
                    ? 'text-gray-300 bg-white/5 border-white/10 hover:bg-white/10' 
                    : 'text-gray-600 bg-white border-gray-200 hover:text-gray-800'
                }`}
              >
                Reset All
              </button>
              <button
                onClick={() => {
                  setActiveFilters(tempFilters);
                  setIsFilterModalOpen(false);
                }}
                className="px-8 py-2.5 text-sm font-bold text-white rounded-xl shadow-lg transition-all active:scale-95 hover:brightness-110"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategory;
